import { ArrowSync20Regular, PlugDisconnected20Regular } from '@fluentui/react-icons';
import { cn } from '@/lib/cn';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { useRealtime } from '@/hooks/useRealtime';

/**
 * Connectivity banner.
 *
 * Two degraded levels:
 *  - offline (`navigator.onLine === false`): nothing will load.
 *  - degraded: the browser is online but the realtime channel dropped, so the
 *    calendar may lag behind until it reconnects.
 */
export function OfflineBanner({ className }: { className?: string }): JSX.Element | null {
  const online = useOnlineStatus();
  const { status } = useRealtime();

  const degraded = online && (status === 'reconnecting' || status === 'offline');

  if (online && !degraded) return null;

  const label = online
    ? 'Actualizaciones en vivo interrumpidas. Reconectando…'
    : 'Sin conexión. Estás viendo la última información cargada.';

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        'flex items-center justify-center gap-2 px-4 py-1.5 text-small font-medium',
        online
          ? 'bg-warning-50 text-warning-700 dark:bg-warning-700/25 dark:text-warning-100'
          : 'bg-danger-50 text-danger-700 dark:bg-danger-700/25 dark:text-danger-100',
        className,
      )}
    >
      {online ? (
        <ArrowSync20Regular aria-hidden="true" className="motion-safe:animate-spin" />
      ) : (
        <PlugDisconnected20Regular aria-hidden="true" />
      )}
      <span>{label}</span>
    </div>
  );
}
